import en from '@/locales/en.json';
import zh from '@/locales/zh.json';
import { isServer } from '@/utils';
import { create } from 'zustand';

export const LocaleTypes = ['en', 'zh'] as const;

export type SupportedLocale = (typeof LocaleTypes)[number];

export type TranslationType = typeof en;

type State = { lang: SupportedLocale; values: TranslationType };

type Actions = { setLang: (lang: string) => void };

const dictionaries = { en, zh };

const getDefaultLang = (): SupportedLocale => {
  const locale = Intl.DateTimeFormat().resolvedOptions().locale;
  const defaultLocale = locale.startsWith('zh') ? 'zh' : 'en';
  if (isServer()) {
    return defaultLocale;
  }
  const lang = localStorage.getItem('ux-preference-language');
  if (lang && LocaleTypes.includes(lang as SupportedLocale)) {
    return lang as SupportedLocale;
  }
  return defaultLocale;
};

const getDefaultValues = () => {
  const lang = getDefaultLang();
  return dictionaries[lang as keyof typeof dictionaries];
};

const useTranslations = create<State & Actions>((set) => ({
  lang: getDefaultLang(),
  values: getDefaultValues() as TranslationType,
  setLang: (lang: string) => {
    let targetLang: string;
    if (LocaleTypes.includes(lang as SupportedLocale)) {
      targetLang = lang;
    } else {
      targetLang = 'en';
    }
    const targetTranslations = dictionaries[targetLang as keyof typeof dictionaries] as TranslationType;

    localStorage.setItem('ux-preference-language', targetLang);
    set(() => {
      return { lang: targetLang as SupportedLocale, values: targetTranslations };
    });
  },
}));

export default useTranslations;
